/**
 * What a Deep research turn draws beyond ordinary steps (boards S2 and S3): the plan, its groups, and the
 * MiniMarv reading each one. Pure: built from the plan part's snapshot and the helper parts' tags, so a live
 * turn, a reloaded one and an eval replay read the same. A turn recorded before the tags existed has no plan,
 * so none of this applies to it and its helpers stay ordinary rows.
 */

/** A group's state, as the server's snapshot says it. */
export type PlanGroupStatus = "waiting" | "reading" | "done";

export interface PlanGroup {
    index: number;
    /** The range the group covers ("01 33 00 to 03 54 13"). */
    label: string;
    size: number;
    status: PlanGroupStatus;
    read: number;
    /** Items found in the group (the rows its MiniMarv recorded). */
    found: number;
    /** The MiniMarv (1, 2, ...) reading or that read it. */
    worker?: number;
}

export interface PlanItem {
    number: number;
    label: string;
    found: number;
    status: string;
}

export interface PlanModel {
    id: string;
    /** What the plan's items are, plural ("specification sections"). */
    noun: string;
    nounOne: string;
    total: number;
    read: number;
    notRead: number;
    found: number;
    /** MiniMarvs that ran at once, at most. */
    workers: number;
    startedAt: number | null;
    endedAt: number | null;
    /** The server's clock when the snapshot was made. */
    at: number | null;
    groups: PlanGroup[];
    items: PlanItem[];
}

function record(value: unknown): Record<string, unknown> | null {
    return typeof value === "object" && value !== null && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : null;
}

const num = (value: unknown, fallback = 0): number =>
    typeof value === "number" && Number.isFinite(value) ? value : fallback;
const text = (value: unknown, fallback = ""): string =>
    typeof value === "string" ? value : fallback;
const optNum = (value: unknown): number | null =>
    typeof value === "number" && Number.isFinite(value) ? value : null;

/** The plan a part's snapshot describes; anything that is not a plan snapshot is null. */
export function parsePlan(value: unknown): PlanModel | null {
    const raw = record(value);
    if (!raw || typeof raw.id !== "string" || typeof raw.total !== "number") return null;
    const groups = (Array.isArray(raw.groups) ? raw.groups : []).flatMap((entry): PlanGroup[] => {
        const group = record(entry);
        if (!group || typeof group.index !== "number") return [];
        const status =
            group.status === "reading" || group.status === "done" ? group.status : "waiting";
        return [
            {
                index: group.index,
                label: text(group.label),
                size: num(group.size),
                status,
                read: num(group.read),
                found: num(group.found),
                ...(typeof group.worker === "number" ? { worker: group.worker } : {}),
            },
        ];
    });
    const items = (Array.isArray(raw.items) ? raw.items : []).flatMap((entry): PlanItem[] =>
        Array.isArray(entry) && typeof entry[0] === "number" && typeof entry[1] === "string"
            ? [{ number: entry[0], label: entry[1], found: num(entry[2]), status: text(entry[3]) }]
            : [],
    );
    return {
        id: raw.id,
        noun: text(raw.noun, "items"),
        nounOne: text(raw.nounOne, "item"),
        total: raw.total,
        read: num(raw.read),
        notRead: num(raw.notRead),
        found: num(raw.found),
        workers: num(raw.workers),
        startedAt: optNum(raw.startedAt),
        endedAt: optNum(raw.endedAt),
        at: optNum(raw.at),
        groups: groups.sort((a, b) => a.index - b.index),
        items,
    };
}

/** "specification sections" -> "sections": the boards name the items without the document kind. */
export function shortNoun(noun: string): string {
    return noun.replace(/^specification\s+/i, "");
}

/** "1 item", "5 items". */
export function itemsText(count: number): string {
    return `${count} ${count === 1 ? "item" : "items"}`;
}

/** "2 min 10 s", "5 min", "45 s". */
export function durationText(ms: number): string {
    const total = Math.max(0, Math.round(ms / 1000));
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    if (minutes === 0) return `${seconds} s`;
    return seconds === 0 ? `${minutes} min` : `${minutes} min ${seconds} s`;
}

const SECTION_ITEM = /^\s*(\d{2})\s?(\d{2})\s?(\d{2})(\.\d+)?\s*[-–—:]?\s*(.*)$/;

/** A plan item as the project writes it: "093200 - Wall Coverings" -> "09 32 00 Wall Coverings". */
export function formatItem(label: string): string {
    const found = SECTION_ITEM.exec(label);
    if (!found) return label.trim();
    const code = `${found[1]} ${found[2]} ${found[3]}${found[4] ?? ""}`;
    return found[5] ? `${code} ${found[5]}` : code;
}

/**
 * The item a helper's lookup names: what its label quotes ('Reading the full text of "093200 - Wall Coverings"'), or
 * the section number it reads ("Reading specification section 331100"). Null for a lookup that names none.
 */
export function lookupItem(label: string): string | null {
    const quoted = /["“]([^"”]+)["”]/.exec(label);
    if (quoted) return quoted[1];
    const section = /\bsection\s+(\d{6}(?:\.\d+)?)\b/i.exec(label);
    return section ? section[1] : null;
}

const SECTION_CODE = /\b(\d{6}(?:\.\d+)?)\b/;

/**
 * The plan item a lookup's words name: the item with the same section number (the plan says
 * "081416 Flush Wood Doors › 1.3" where a lookup says "081416 - Flush Wood Doors"), else the same words.
 */
export function findPlanItem(plan: PlanModel, named: string | null): PlanItem | undefined {
    if (!named) return undefined;
    const code = SECTION_CODE.exec(named)?.[1];
    if (code) return plan.items.find((item) => SECTION_CODE.exec(item.label)?.[1] === code);
    return plan.items.find((item) => item.label === named);
}

/** One row of the plan block: a group, or a run of groups still waiting. */
export type PlanRow =
    | { kind: "group"; group: PlanGroup }
    | { kind: "waiting"; from: number; to: number; sections: number };

/** The plan block's rows: every group that has started, then each run of waiting groups as one row. */
export function planRows(plan: PlanModel): PlanRow[] {
    const rows: PlanRow[] = [];
    let run: PlanGroup[] = [];
    const flush = () => {
        if (run.length === 0) return;
        if (run.length === 1) rows.push({ kind: "group", group: run[0] });
        else
            rows.push({
                kind: "waiting",
                from: run[0].index,
                to: run[run.length - 1].index,
                sections: run.reduce((n, group) => n + group.size, 0),
            });
        run = [];
    };
    for (const group of plan.groups) {
        if (group.status === "waiting") run.push(group);
        else {
            flush();
            rows.push({ kind: "group", group });
        }
    }
    flush();
    return rows;
}

/**
 * How long the work has run: to its end, else (a live turn) as of the snapshot plus `sinceSnapshot` ms (the time the
 * client has held it), so a client clock that differs from the server's never shows. Null when there is no start.
 */
export function planDuration(plan: PlanModel, sinceSnapshot: number): number | null {
    if (plan.startedAt === null) return null;
    if (plan.endedAt !== null) return Math.max(0, plan.endedAt - plan.startedAt);
    return Math.max(0, (plan.at ?? plan.startedAt) - plan.startedAt + sinceSnapshot);
}

const VERBS: Record<string, [gerund: string, past: string]> = {
    read: ["reading", "read"],
    find: ["finding", "found"],
    list: ["listing", "listed"],
    check: ["checking", "checked"],
    compare: ["comparing", "compared"],
    count: ["counting", "counted"],
    search: ["searching", "searched"],
    open: ["opening", "opened"],
    extract: ["extracting", "extracted"],
    review: ["reviewing", "reviewed"],
    research: ["researching", "researched"],
    collect: ["collecting", "collected"],
    gather: ["gathering", "gathered"],
    scan: ["scanning", "scanned"],
    identify: ["identifying", "identified"],
    look: ["looking", "looked"],
    pull: ["pulling", "pulled"],
    summarize: ["summarizing", "summarized"],
    analyze: ["analyzing", "analyzed"],
    assess: ["assessing", "assessed"],
    locate: ["locating", "located"],
    group: ["grouping", "grouped"],
    compile: ["compiling", "compiled"],
    determine: ["determining", "determined"],
    verify: ["verifying", "verified"],
    confirm: ["confirming", "confirmed"],
    examine: ["examining", "examined"],
    inspect: ["inspecting", "inspected"],
    survey: ["surveying", "surveyed"],
    get: ["getting", "got"],
    fetch: ["fetching", "fetched"],
};

/**
 * What a MiniMarv did, in words, from the job Marvin gave it ("Read the change notes for every sheet in
 * Bulletin 1"): "read the change notes ..." when finished, "reading the change notes ..." while it runs. Null
 * when the job does not open with a verb the client knows, so the row falls back to the helper's lookups.
 */
export function helperPhrase(task: string | undefined, running: boolean): string | null {
    const words = (task ?? "").trim();
    const match = /^([A-Za-z]+)\s+(.+)$/s.exec(words);
    if (!match) return null;
    const verb = match[1].toLowerCase();
    const forms = VERBS[verb];
    return forms ? `${forms[running ? 0 : 1]} ${match[2]}` : null;
}
